import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, IconButton, Menu, MenuItem as MUIMenuItem } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { MouseEvent } from 'react';
import { useMemo, useState } from 'react';
import { Analytics } from '@/shared/lib';
import { DefaultPresetMenuItem } from '../DefaultPresetMenuItem/DefaultPresetMenuItem';
import { usePresetDeleteDialog } from '../DeletePresetDialog';
import { EditPresetDialog } from '../EditPresetDialog';
import { useSavedPresetsContext } from '../SavedPresetsContext/SavedPresetsContext';

const MenuItem = styled(MUIMenuItem)(({ theme }) => ({
  padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
}));

interface Props {
  presetId: string;
}

export function SavedPresetMenu({ presetId }: Props) {
  const { deletePreset, sharePreset, unsharePreset, presets, updatePreset } =
    useSavedPresetsContext();
  const preset = useMemo(() => {
    return presets.find((p) => p.id === presetId);
  }, [presets, presetId]);
  const isShared = Boolean(preset?.share);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const { openDeleteDialog, deleteDialogElement } = usePresetDeleteDialog({
    deletePreset,
  });
  const open = Boolean(anchorEl);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (event: MouseEvent) => {
    event.stopPropagation();
    setAnchorEl(null);
  };
  const handleEditPreset = () => {
    setEditDialogOpen(true);
    Analytics.sendFeatureUsage('saved_presets', 'edit_preset');
  };
  const handleDeleteView = () => {
    if (preset) openDeleteDialog(preset);
    Analytics.sendFeatureUsage('saved_presets', 'delete_preset');
  };
  const handleShare = () => {
    sharePreset(presetId);
    Analytics.sendFeatureUsage('saved_presets', 'share_preset');
  };
  const handleUnshare = () => {
    unsharePreset(presetId);
    Analytics.sendFeatureUsage('saved_presets', 'unshare_preset');
  };

  return (
    <Box>
      <IconButton size="small" onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu
        MenuListProps={{
          sx: { p: 0 },
        }}
        anchorEl={anchorEl}
        open={open}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        onClose={handleClose}
        onClick={handleClose}
      >
        <MenuItem onClick={handleEditPreset}>Edit preset</MenuItem>
        <DefaultPresetMenuItem presetId={presetId} />
        {isShared ? (
          <MenuItem onClick={handleUnshare}>Unshare</MenuItem>
        ) : (
          <MenuItem onClick={handleShare}>Share</MenuItem>
        )}
        <MenuItem onClick={handleDeleteView}>Delete</MenuItem>
      </Menu>
      {editDialogOpen && preset && (
        <EditPresetDialog
          preset={preset}
          onClose={() => setEditDialogOpen(false)}
          updatePreset={updatePreset}
        />
      )}
      {deleteDialogElement}
    </Box>
  );
}
